import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  where,
} from "firebase/firestore";

// ShopMy places gifting orders on a relay address (gifting+NNNNNN@shopmyshelf.us) and
// leaves this placeholder as the contract owner, so the Lifetime Loftie+ grant lands on
// an address the creator cannot log into and their app never activates.
export const SHOPMY_PLACEHOLDER_NAME = "ShopMy, Inc.";

export const isStranded = (contract) =>
  contract?.user?.name === SHOPMY_PLACEHOLDER_NAME;

// A grant is the Loftie+ that came with an order: loftie-api writes it at order time with a
// Lifetime or Annual `type` and no billing policy. A paid subscription is the mirror image,
// billing_policy from the Shopify sync and no `type`. Only grants may be applied from here.
// Moving a paying subscriber's contract has Shopify-side consequences this page cannot see,
// so that stays a by-hand job.
export const isGrant = (contract) => {
  const type = String(contract?.type || "").toUpperCase();
  return (
    (type === "LIFETIME" || type === "ANNUAL") && !contract?.billing_policy
  );
};

// Anything this page may act on: a stranded ShopMy gift, or any other order grant.
export const canApply = (contract) => isStranded(contract) || isGrant(contract);

// The term comes off the grant's own `type`. Paid contracts carry billing_policy instead,
// written verbatim by the Shopify sync. Never infer a term from a selling plan name.
// A contract with neither is ambiguous, and the safe answer is to refuse rather than to
// hand out the most valuable term by default.
export const membershipTypeFor = (contract) => {
  const type = String(contract?.type || "").toUpperCase();
  if (type === "LIFETIME") return "Lifetime";
  if (type === "ANNUAL") return "Annual";
  if (contract?.billing_policy) return "Monthly";
  return null;
};

// The app and the nightly cancellation job both read next_billing_date as a string.
// A Firestore Timestamp landing there crashes the Flutter subscription page.
const toIsoString = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Put a Loftie+ grant onto the app account the customer actually uses.
 *
 * The common case is not ShopMy at all: someone buys a clock on one address and signs up in
 * the app on another (a second alias, an Apple relay, a typo) and the membership never
 * switches on, because nothing ties the two together. The fix is the same as for a stranded
 * gift: point the contract at the address they log in with, and turn the membership on for
 * that account. When the addresses already match, only the user doc changes.
 *
 * Two documents have to change together. Re-pointing the contract alone leaves the app on
 * the activation screen; writing the user doc alone leaves the contract orphaned. Both go
 * in one transaction, so a failure can never leave the contract moved with no membership
 * behind it, which would also drop a ShopMy gift out of the stranded list for good.
 *
 * The transaction re-reads the contract and refuses if it is not a grant, or if its owner is
 * no longer the one the operator was looking at. A double submit or a stale screen cannot
 * move a live subscription, and cannot move a grant twice.
 *
 * A customer with no app account is REFUSED by default (error.code = "no_app_account"): a grant
 * on an address nobody can log into sits dormant and looks identical to one that worked
 * (research loftie-plus-link-verification-gap-2026-08). Pass allowNoAppAccount: true only after
 * the operator has confirmed the customer will sign up with exactly that address; the contract
 * is then stamped granted_without_app_account so the dormant state is visible.
 */
export const NO_APP_ACCOUNT = "no_app_account";

export async function applyGrantToAccount(
  db,
  { contractId, email, name, expectedEmail, allowNoAppAccount = false }
) {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();
  const newName = String(name || "").trim();

  if (!normalizedEmail) throw new Error("Enter the customer's email address.");

  // Rows arrive with a numeric id when the contract document carries its own `id`
  // field; doc() only accepts strings.
  const contractRef = doc(db, "subscription-contracts", String(contractId));

  // Client transactions cannot run queries, so resolve the account first.
  const userMatches = await getDocs(
    query(collection(db, "users"), where("email", "==", normalizedEmail))
  );
  if (userMatches.size > 1) {
    throw new Error(
      `${userMatches.size} app accounts use ${normalizedEmail}. Resolve by hand — refusing to guess which one.`
    );
  }
  const userId = userMatches.size === 1 ? userMatches.docs[0].id : null;

  let membershipType = null;
  let moved = false;
  let finalName = newName;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(contractRef);
    if (!snap.exists()) throw new Error(`No contract ${contractId}.`);
    const contract = snap.data();
    const stranded = isStranded(contract);
    const currentEmail = String(contract.user?.email || "")
      .trim()
      .toLowerCase();

    if (!stranded && !isGrant(contract)) {
      throw new Error(
        `Contract ${contractId} is a paid subscription, not a Loftie+ grant. It belongs to ` +
          `${contract.user?.email || "someone else"} and cannot be moved from here. Nothing was changed.`
      );
    }

    if (
      expectedEmail !== undefined &&
      currentEmail !== String(expectedEmail || "").trim().toLowerCase()
    ) {
      throw new Error(
        `Contract ${contractId} is now on ${contract.user?.email || "(no email)"}, not ` +
          `${expectedEmail || "(no email)"}. It changed since this page loaded. Nothing was changed.`
      );
    }

    if (stranded && !newName) {
      throw new Error(
        "Enter the creator's name. The contract still carries the ShopMy placeholder."
      );
    }

    membershipType = membershipTypeFor(contract);
    if (!membershipType) {
      throw new Error(
        `Contract ${contractId} has no membership term on it (no type, no billing policy). ` +
          `Refusing to guess. Nothing was changed.`
      );
    }

    moved = currentEmail !== normalizedEmail;

    // No app account on the target address: refuse (error.code no_app_account) unless the
    // operator confirmed the override. Checked here, after every contract-state refusal
    // above, so a confirmed override is never followed by a different "nothing changed".
    if (!userId && !allowNoAppAccount) {
      const err = new Error(
        `No Loftie app account uses ${normalizedEmail}. A grant on that address would sit dormant ` +
          `until the customer signs up with exactly that address and uses “Link your purchase”, and ` +
          `nothing would show that it is waiting. Nothing was changed.`
      );
      err.code = NO_APP_ACCOUNT;
      throw err;
    }
    if (!moved && !userId && contract.granted_without_app_account === true) {
      throw new Error(
        `The contract is already on ${normalizedEmail}, already marked as granted without an app ` +
          `account (${contract.granted_without_app_account_at || "date unknown"}), and still no app ` +
          `account uses that address. Nothing to change.`
      );
    }

    // Dotted paths update the leaves; writing `user` as a map would drop its siblings.
    const contractUpdate = {};
    if (moved) contractUpdate["user.email"] = normalizedEmail;
    if (newName) contractUpdate["user.name"] = newName;
    if (!userId) {
      // Operator chose to grant to an address with no app account: record that fact on
      // the contract so the dormant state is visible instead of indistinguishable.
      contractUpdate["granted_without_app_account"] = true;
      contractUpdate["granted_without_app_account_at"] = new Date().toISOString();
    } else if (contract.granted_without_app_account === true) {
      // The account exists now: the grant is no longer waiting. Clear the mark and keep
      // the moment it linked.
      contractUpdate["granted_without_app_account"] = deleteField();
      contractUpdate["granted_without_app_account_linked_at"] = new Date().toISOString();
    }
    if (Object.keys(contractUpdate).length > 0) {
      tx.update(contractRef, contractUpdate);
    }
    finalName = newName || contract.user?.name || "";

    if (userId) {
      const fields = {
        "loftie-plus-membership": true,
        membership_status: "ACTIVE",
        membership_type: membershipType,
        subscriptionSource: "SHOPIFY",
        // A stopgap "free months" grant from the User page leaves an end date behind, and
        // both the nightly cancel job and the app's subscription page read it. The real
        // term lives on the contract now, so clear it rather than let it end the membership.
        membership_inactive_date: deleteField(),
      };
      const nextBilling = toIsoString(contract.nextBillingDate);
      if (nextBilling) fields.next_billing_date = nextBilling;
      tx.update(doc(db, "users", userId), fields);
    }
  });

  // The transaction is all-or-nothing, so this confirms rather than repairs. Still read
  // back: telling a customer they are fixed when they are not is the failure that matters.
  const contractAfter = (await getDoc(contractRef)).data();
  if (
    String(contractAfter?.user?.email || "").toLowerCase() !== normalizedEmail
  ) {
    throw new Error(
      "The contract did not take the new email. Do not tell the customer it is fixed."
    );
  }
  if (userId) {
    const userAfter = (await getDoc(doc(db, "users", userId))).data();
    if (
      userAfter?.["loftie-plus-membership"] !== true ||
      userAfter?.membership_status !== "ACTIVE"
    ) {
      throw new Error(
        "The membership fields did not stick. Do not tell the customer it is fixed."
      );
    }
  }

  return {
    email: normalizedEmail,
    name: finalName,
    membershipType,
    linkedAppAccount: Boolean(userId),
    userId,
    moved,
  };
}

// The stranded-gift list grew up calling this by its old name.
export const reassignContractToCreator = applyGrantToAccount;
